import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Typography, Spin, Button } from 'antd';
import { LeftOutlined, TeamOutlined } from '@ant-design/icons';
import { getMoodlePrograms, getManualCourseProgress } from '../services/moodleService';
import { getTrainerLedLeaderboard, getTrainingLeaderboard } from '../services/trainingService';
import { useAuth } from '../hooks/useAuth';
import Leaderboard from '../components/Leaderboard';
import ParticipantList from '../components/ParticipantList';
import { deriveUngradedParticipants } from '../utils/moduleUtils';
import { Training, LeaderboardEntry, UngradedParticipant } from '../types/training';

const { Title, Text } = Typography;

const StakeholderProgramProgress: React.FC = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState<boolean | null>(null);
    const [program, setProgram] = useState<Training | null>(null);
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [ungradedParticipants, setUngradedParticipants] = useState<UngradedParticipant[]>([]);

    useEffect(() => {
        if (!user?.username || !trainingId) return;
        setLoading(true);
        getMoodlePrograms(user.username)
            .then(async (programs: Training[]) => {
                const match = programs.find((p) => p.trainingId === trainingId);
                // Access is checked per-program, not just "is a stakeholder somewhere" -
                // being a stakeholder on a different program must not grant access here.
                if (!match || !match.isStakeholder) {
                    setAuthorized(false);
                    return;
                }
                setAuthorized(true);
                setProgram(match);

                const isTrainerLedProgram = match.programType === 'TrainerLed';
                const entries = isTrainerLedProgram
                    ? await getTrainerLedLeaderboard(match.courseId as number)
                    : await getTrainingLeaderboard(match.trainingId);
                setLeaderboard(entries || []);

                // Trainer-led's leaderboard query excludes anyone with zero graded
                // assignments, so it can come back empty despite real enrollment -
                // fall back to a plain submitted/total participant list in that case.
                if (isTrainerLedProgram && (!entries || entries.length === 0)) {
                    const courseProgress = await getManualCourseProgress(match.courseId as number);
                    setUngradedParticipants(deriveUngradedParticipants(courseProgress));
                }
            })
            .catch((error) => {
                console.error('Error loading stakeholder program progress', error);
                setAuthorized(false);
            })
            .finally(() => setLoading(false));
    }, [user, trainingId]);

    if (loading || authorized === null) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!authorized) {
        return <Navigate to="/stakeholder" replace />;
    }

    const isTrainerLed = program?.programType === 'TrainerLed';

    return (
        <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Button icon={<LeftOutlined />} onClick={() => navigate('/stakeholder')} />
                <Title level={3} style={{ margin: 0 }}>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    {program?.trainingName}
                </Title>
            </div>
            <Text type="secondary">Read-only progress summary - individual submissions and evaluations aren't shown here.</Text>

            <div style={{ marginTop: 24 }}>
                {isTrainerLed && leaderboard.length === 0 && ungradedParticipants.length > 0 ? (
                    <ParticipantList
                        participants={ungradedParticipants}
                        emptyDescription="No participants enrolled yet."
                    />
                ) : (
                    <Leaderboard
                        entries={leaderboard}
                        variant={isTrainerLed ? 'trainerLed' : 'aiLed'}
                        emptyDescription="No graded progress yet - scores will appear here once participants are graded."
                    />
                )}
            </div>
        </div>
    );
};

export default StakeholderProgramProgress;
